import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Home, DollarSign, Bed, Bath, MapPin, ArrowLeft, FileText, Calendar, Plus, Upload, Loader2, Trash2, ExternalLink, Paperclip } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentPropertyDetail = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const { token } = useAuth();
  const [property, setProperty] = useState(null);
  const [openHouses, setOpenHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [propertyFiles, setPropertyFiles] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [eventForm, setEventForm] = useState({
    date: '',
    time: '13:00',
    end_time: '16:00',
    description: ''
  });

  useEffect(() => {
    fetchProperty();
  }, [propertyId]);

  const fetchProperty = async () => {
    try {
      const [propertyRes, eventsRes] = await Promise.all([
        axios.get(`${API}/agent/properties/${propertyId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/events`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setProperty(propertyRes.data);
      
      // Filter events for this specific property
      const propertyEvents = eventsRes.data.filter(
        event => event.property_address === propertyRes.data.address
      );
      setOpenHouses(propertyEvents);
    } catch (error) {
      toast.error('Failed to fetch property details');
      navigate('/agent/properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyFiles = async () => {
    try {
      const res = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { property_id: propertyId }
      });
      setPropertyFiles(res.data);
    } catch (err) {
      console.error('Failed to fetch files');
    }
  };

  useEffect(() => {
    if (propertyId) fetchPropertyFiles();
  }, [propertyId]);

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
    setUploading(true);
    let successCount = 0;
    for (const file of selectedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('property_id', propertyId);
        await axios.post(`${API}/files/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (err) {
        toast.error(`Failed: ${file.name} — ${err.response?.data?.detail || 'Upload error'}`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} file(s) uploaded`);
      fetchPropertyFiles();
    }
    setUploading(false);
    setShowUploadDialog(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await axios.delete(`${API}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted');
      fetchPropertyFiles();
    } catch (err) {
      toast.error('Failed to delete file');
    }
  };

  const handleEventFormChange = (e) => {
    setEventForm({
      ...eventForm,
      [e.target.name]: e.target.value
    });
  };

  const handleCreateOpenHouse = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, {
        property_address: property.address,
        date: eventForm.date,
        time: eventForm.time,
        end_time: eventForm.end_time,
        description: eventForm.description || `Open house for ${property.address}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Open house created successfully!');
      setShowCreateEvent(false);
      setEventForm({
        date: '',
        time: '13:00',
        end_time: '16:00',
        description: ''
      });
      
      // Refresh the property data to show new open house
      fetchProperty();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create open house');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-12 text-center">
          <p className="text-stone-600">Loading property details...</p>
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-12 text-center">
          <p className="text-stone-600">Property not found</p>
          <Button onClick={() => navigate('/agent/properties')} className="mt-4">
            Back to Properties
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/agent/properties')}
            className="mb-4 text-stone-600 hover:text-[#1A4D2E]"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Properties
          </Button>
        </div>

        {/* Property Details Card */}
        <Card className="bg-white p-4 sm:p-8 rounded-2xl card-shadow mb-6">
          <div className="flex items-start gap-3 sm:gap-6">
            <div className="p-3 sm:p-4 rounded-full bg-[#1A4D2E]/10 shrink-0">
              <Home className="text-[#1A4D2E]" size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mb-3 break-words" style={{fontFamily: '"Playfair Display", serif'}}>
                {property.address}
              </h1>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="text-green-600 shrink-0" size={18} />
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wider">Price</p>
                    <p className="text-base sm:text-xl font-bold text-stone-800">
                      ${property.price.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Bed className="text-blue-600 shrink-0" size={18} />
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wider">Bedrooms</p>
                    <p className="text-base sm:text-xl font-bold text-stone-800">{property.bedrooms}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Bath className="text-purple-600 shrink-0" size={18} />
                  <div>
                    <p className="text-xs text-stone-500 uppercase tracking-wider">Bathrooms</p>
                    <p className="text-base sm:text-xl font-bold text-stone-800">{property.bathrooms}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="text-orange-600 shrink-0" size={18} />
                  <div className="min-w-0">
                    <p className="text-xs text-stone-500 uppercase tracking-wider">MLS #</p>
                    <p className="text-base sm:text-xl font-bold text-stone-800 truncate">{property.mls_number}</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-stone-500 uppercase tracking-wider mb-2">Property Type</p>
                  <p className="text-lg text-stone-800 capitalize">{property.property_type || 'Not specified'}</p>
                </div>

                <div>
                  <p className="text-sm text-stone-500 uppercase tracking-wider mb-2">Status</p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                    property.status === 'available' ? 'bg-green-100 text-green-800' :
                    property.status === 'sold' ? 'bg-red-100 text-red-800' :
                    'bg-orange-100 text-orange-800'
                  }`}>
                    {property.status?.toUpperCase() || 'AVAILABLE'}
                  </span>
                </div>
              </div>

              {/* Property Files Section */}
              <div className="mt-6 pt-4 border-t border-stone-200">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm text-stone-500 uppercase tracking-wider flex items-center gap-2">
                    <Paperclip size={14} />
                    Attachments
                  </p>
                  <Button
                    size="sm"
                    onClick={() => setShowUploadDialog(true)}
                    className="bg-[#1A4D2E] text-white hover:bg-[#143D24] rounded-full h-8 px-4 text-xs"
                    data-testid="property-upload-btn"
                  >
                    <Upload size={14} className="mr-1" />
                    Upload File
                  </Button>
                </div>
                {propertyFiles.length > 0 ? (
                  <div className="space-y-2">
                    {propertyFiles.map((f) => (
                      <div key={f.id} className="flex items-center justify-between p-2.5 bg-stone-50 rounded-lg border border-stone-200 group" data-testid={`file-item-${f.id}`}>
                        <a
                          href={f.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm text-[#1A4D2E] hover:underline font-medium truncate flex-1 mr-3"
                          data-testid={`file-link-${f.id}`}
                        >
                          <ExternalLink size={14} className="flex-shrink-0" />
                          {f.original_filename}
                        </a>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteFile(f.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`file-delete-${f.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-400 text-sm">No files attached to this property.</p>
                )}
              </div>

              {property.description && (
                <div className="mt-6">
                  <p className="text-sm text-stone-500 uppercase tracking-wider mb-2">Description</p>
                  <p className={`text-stone-700 leading-relaxed ${!isDescriptionExpanded ? 'line-clamp-3' : ''}`}>
                    {property.description}
                  </p>
                  {property.description.length > 200 && (
                    <button
                      onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                      className="text-[#1A4D2E] hover:underline text-sm mt-2 font-medium"
                    >
                      {isDescriptionExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Open Houses Section */}
        <Card className="bg-white p-8 rounded-2xl card-shadow">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[#1A4D2E]" style={{fontFamily: '"Playfair Display", serif'}}>
              Open Houses
            </h2>
            <Button
              onClick={() => setShowCreateEvent(true)}
              className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full h-12 px-6"
            >
              <Plus className="mr-2" size={20} />
              Create Open House
            </Button>
          </div>

          {openHouses.length === 0 ? (
            <div className="text-center py-12 bg-stone-50 rounded-xl">
              <Calendar className="mx-auto text-stone-300 mb-4" size={64} />
              <p className="text-stone-500 text-lg mb-2">No open houses scheduled</p>
              <p className="text-stone-400 text-sm">Create your first open house for this property</p>
            </div>
          ) : (
            <div className="space-y-4">
              {openHouses.map((event) => (
                <div
                  key={event.id}
                  className="border-2 border-stone-200 rounded-xl p-6 hover:border-[#1A4D2E] hover:shadow-md transition-all cursor-pointer"
                  onClick={() => navigate(`/event/${event.id}`)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-stone-800 mb-2">
                        {event.description || 'Open House'}
                      </h3>
                      <div className="space-y-1 text-stone-600">
                        <p className="flex items-center gap-2">
                          <Calendar size={16} />
                          <span>{(() => { const [y,m,d] = event.date.split('-'); return new Date(y, m-1, d).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          }); })()}</span>
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="ml-6">{event.time} - {event.end_time}</span>
                        </p>
                      </div>
                    </div>
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                      (() => { const [y,m,d] = event.date.split('-'); return new Date(y, m-1, d); })() < new Date() 
                        ? 'bg-stone-100 text-stone-600'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {(() => { const [y,m,d] = event.date.split('-'); return new Date(y, m-1, d); })() < new Date() ? 'Completed' : 'Upcoming'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Create Open House Modal */}
        <Dialog open={showCreateEvent} onOpenChange={setShowCreateEvent}>
          <DialogContent className="max-w-[90vw] sm:max-w-md max-h-[85vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader className="pb-2">
              <DialogTitle className="text-lg">Create Open House</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateOpenHouse} className="space-y-2.5">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">
                  Property Address
                </Label>
                <Input
                  value={property.address}
                  disabled
                  className="h-9 bg-stone-100 border border-stone-200 rounded-lg text-xs px-2.5"
                />
              </div>

              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">
                  Date *
                </Label>
                <Input
                  type="date"
                  name="date"
                  value={eventForm.date}
                  onChange={handleEventFormChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="h-9 bg-stone-50 border border-stone-200 rounded-lg text-xs px-2.5"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">
                    Start Time *
                  </Label>
                  <Input
                    type="time"
                    name="time"
                    value={eventForm.time}
                    onChange={handleEventFormChange}
                    className="h-9 bg-stone-50 border border-stone-200 rounded-lg text-xs px-2"
                    required
                  />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">
                    End Time *
                  </Label>
                  <Input
                    type="time"
                    name="end_time"
                    value={eventForm.end_time}
                    onChange={handleEventFormChange}
                    className="h-9 bg-stone-50 border border-stone-200 rounded-lg text-xs px-2"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1 block">
                  Description (Optional)
                </Label>
                <Textarea
                  name="description"
                  value={eventForm.description}
                  onChange={handleEventFormChange}
                  placeholder="Additional details..."
                  className="min-h-[60px] bg-stone-50 border border-stone-200 rounded-lg text-xs px-2.5 py-2 resize-none"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateEvent(false)}
                  className="flex-1 h-9 text-xs border border-stone-300 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-9 text-xs bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full"
                >
                  Create
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* File Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Files</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-stone-600">
                Select files to attach to this property.
              </p>
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileUpload}
                disabled={uploading}
                className="cursor-pointer"
                data-testid="file-upload-input"
              />
              {uploading && (
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <Loader2 size={16} className="animate-spin" />
                  Uploading...
                </div>
              )}
              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AgentPropertyDetail;
